import { Component, OnInit, inject } from '@angular/core';
import { ApplicationConfigService } from '../services/application-config.service';
import { UserService } from '../services/user/user.service';
import { BaseComponent } from '@nucleus/ui-base';
import { AuthenticationService } from '../services/authentication.service';
import { ChangePasswordComponent } from '../settings/change-password/change-password.component';
import { ThemeService } from '@nucleus/ui-common';
import { User } from '@nucleus/user-ui';
import { AboutUsComponent } from '../layout/about-us/about-us.component';
import { PrivacyComponent } from '../layout/privacy/privacy.component';
import { TermsComponent } from '../layout/terms/terms.component';
import { SupportComponent } from '../layout/support/support.component';
import { AppInterceptorService } from '../services/app-interceptor.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.css',
    standalone: false
})
export class HeaderComponent extends BaseComponent implements OnInit {

  appConfigService : ApplicationConfigService = inject(ApplicationConfigService);
  authenticationService: AuthenticationService = inject(AuthenticationService);
  themeService: ThemeService = inject(ThemeService);

  userService : UserService = inject(UserService);
  user: User = {} as User;

  constructor() {
    super();
  }

  /** NUC-152: opt-in feature flag (set by ui-config.json features.org-units). */
  isOrgUnitsEnabled(): boolean {
    return localStorage.getItem('feature.org-units') === 'true';
  }

  /** UserData type-administration screens — same opt-in pattern. */
  isUserDataTypesEnabled(): boolean {
    return localStorage.getItem('feature.userdata-types') === 'true';
  }

  ngOnInit(): void {
    this.userService.getMyProfile({
      onStart: () => {},
      onSuccess: (user: User) => { this.user = user; },
      onError: () => {},
      onComplete: () => {}
    });
  }

  navigate(route: string) : void {
    this.router.navigate([route]);
  }

  onLogout() : void {
    // Match goldfish/bookwise pattern: redirect on BOTH success and error so the
    // user is always kicked to the OAuth login flow, even if the backend logout
    // call returns 401 (which happens when the access token has already expired
    // — common cause of "logout button does nothing" reports). TASK-124.
    this.userService.logout(
      {
        onStart: () => {},
        onSuccess: (result: void) => {
          this.appConfigService.clear();
          localStorage.clear();
          sessionStorage.clear();
          this.router.navigate(["/"]);
          this.authenticationService.authenticate();
        },
        onError: (error: string) => {
          // Even if backend logout failed, clear client state and force the
          // OAuth redirect so the user is no longer perceived as logged in.
          console.log(`logout error: ${error}`);
          this.appConfigService.clear();
          localStorage.clear();
          sessionStorage.clear();
          this.authenticationService.authenticate();
        },
        onComplete: () => {}
      }
    );
  }

  onChangePassword() : void {
    this.dialog.open(ChangePasswordComponent, {
      panelClass: 'bw-dialog-panel',
      width: '500px',
      maxWidth: '95vw',
      data: { }
    });
  }

  // ── Help menu — replaces the standalone app-footer (TASK-124 / TASK-125).
  // About / Privacy / Terms ported in TASK-125 C-1. Support stays toasted
  // until C-2 (depends on AppInterceptorService.getOrCreateSessionId).

  showAbout(): void {
    this.dialog.open(AboutUsComponent, {
      panelClass: 'bw-dialog-panel',
      width: '680px', maxWidth: '90vw', maxHeight: '85vh'
    });
  }

  showSupport(): void {
    this.dialog.open(SupportComponent, {
      panelClass: 'bw-dialog-panel',
      width: '520px', maxWidth: '90vw', autoFocus: false,
      data: {
        userEmail: this.appConfigService.getUserId(),
        tenant: this.appConfigService.getUserId(),
        sessionId: AppInterceptorService.getOrCreateSessionId(),
        appVersion: localStorage.getItem('version')
      }
    });
  }

  showPrivacy(): void {
    this.dialog.open(PrivacyComponent, {
      panelClass: 'bw-dialog-panel',
      width: '680px', maxWidth: '90vw', maxHeight: '85vh'
    });
  }

  showTerms(): void {
    this.dialog.open(TermsComponent, {
      panelClass: 'bw-dialog-panel',
      width: '680px', maxWidth: '90vw', maxHeight: '85vh'
    });
  }

  /** Two-letter initials for the avatar disc shown in the header user button. */
  getUserInitials(): string {
    const name = (this.user?.name || this.user?.username || this.appConfigService.getUserId() || '').trim();
    if (!name) return '?';
    const parts = name.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
}
